import { DropdownButton, FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { useDragOver } from '@minoru/react-dnd-treeview';
import cx from 'classnames';
import { RestrictionsBadge } from 'collection/pages/collectionEdit/components/restrictionsBadge';
import { Category } from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { RefObject } from 'react';

import { NodeLockIconWithTooltip } from 'components/icons/nodeLockIcon/nodeLockIcon';

import { DragHandle } from '../dragHandle';

import CategoryInsertsMenu from './categoryInsertsMenu';
import CategoryMenu from './categoryMenu';
import s from './styles.module.scss';

type Props = {
  node: Category;
  handleRef?: RefObject<HTMLSpanElement>;
  isDragging?: boolean;
  isPreview?: boolean;
  isOpen: boolean;
  onToggleOpen: () => void;
  depth: number;
};

const CategoryNode = ({
  node,
  isDragging,
  isPreview,
  handleRef,
  isOpen,
  onToggleOpen,
  depth,
}: Props): JSX.Element => {
  const categoryStyles = cx(s.categoryNode, {
    [s.nodeIsDragging]: isPreview,
    [s.isOpen]: isOpen,
    [s.isSyncedWithElevio]: node.isSyncedWithElevio,
  });

  const dropdownStyles = cx(isDragging && s.isHidden);
  const nodeTitle = extractTitleFromTranslations(node.titles);

  // Don't open yourself when dragging over.
  const dragOverHandler = () => {
    if (isDragging) return;
    onToggleOpen();
  };
  const dragOverProps = useDragOver(node.id, isOpen, dragOverHandler);

  return (
    <div
      className={categoryStyles}
      style={{
        marginLeft: `${depth * 32}px`,
      }}
      {...dragOverProps}
    >
      <FlexBox
        height="100%"
        justifyContent="space-between"
        alignItems="center"
        className={s.CategoryNode}
      >
        <FlexBox
          alignItems="center"
          style={{ position: 'relative' }}
          className={s.categoryNodeClickable}
        >
          {!node.isSyncedWithElevio && (
            <DragHandle
              ref={handleRef}
              onDragStart={() => isOpen && onToggleOpen()}
            />
          )}
          <FlexBox
            alignItems="center"
            onClick={onToggleOpen}
            as="a"
            data-testid="category-node"
          >
            <span className={s.accordionToggle}>
              <Icon name={`angle-${isOpen ? 'down' : 'right'}`} />
            </span>
            <Text.Heading className={s.categoryTitle} variant="heading-3">
              {nodeTitle}
            </Text.Heading>
          </FlexBox>
        </FlexBox>

        <FlexBox alignItems="center" gap="200">
          <RestrictionsBadge restrictions={node.restrictions} />
          {node.isSyncedWithElevio && (
            <NodeLockIconWithTooltip label="Synced from Elevio" />
          )}
          {!node.isSyncedWithElevio && (
            <>
              <DropdownButton
                className={dropdownStyles}
                icon="plus"
                buttonProps={{ variant: 'tertiary' }}
                dropdown={<CategoryInsertsMenu category={node} depth={depth} />}
              />
              <DropdownButton
                className={dropdownStyles}
                icon="dots-h"
                buttonProps={{ variant: 'tertiary' }}
                dropdown={<CategoryMenu category={node} depth={depth} />}
              />
            </>
          )}
        </FlexBox>
      </FlexBox>
    </div>
  );
};

export default CategoryNode;
